import React from 'react';
import { logError } from '@utils/usefulFuncs';
import { validateDefinition } from './validate';
import { providesFor } from './specSchema';
import { changesBySection } from './diffPaths';

// One Context for the draft editor. Every question panel is a thin consumer over pure children,
// per the house pattern — without this, `activePath`, the problem list and the setter would all
// prop-drill four levels through the panels and the Studio.
//
// The Context OWNS NO BUFFER. `useSpecEditor` still owns definition / isDirty / save / revert; this
// only carries them down plus the derived views (problems, provides, changes) so each is computed
// once per buffer change rather than once per panel.

const missingProvider = (name) => () =>
  logError(`SpecEditorContext: ${name} called with no SpecEditorProvider mounted`);

const defaultState = {
  definition: {},
  setValue: missingProvider('setValue'),
  removeValue: missingProvider('removeValue'),
  replaceSection: missingProvider('replaceSection'),
  problems: [],
  provides: [],
  changesBySection: {},
  seedOrigin: null,
  activePath: null,
  goToPath: missingProvider('goToPath'),
  expandedPanels: {},
  togglePanel: missingProvider('togglePanel'),
  expandPanel: missingProvider('expandPanel'),
  panelModes: {},
  setPanelMode: missingProvider('setPanelMode'),
  rawText: {},
  setRawText: missingProvider('setRawText'),
  bufferEpoch: 0,
  savedEpoch: 0,
  unparsedPanels: [],
  previewRun: null,
  setPreviewRun: missingProvider('setPreviewRun'),
};

export const SpecEditorContext = React.createContext(defaultState);

const ACTIONS = {
  TOGGLE_PANEL: 'TOGGLE_PANEL',
  EXPAND_PANEL: 'EXPAND_PANEL',
  SET_PANEL_MODE: 'SET_PANEL_MODE',
  GO_TO_PATH: 'GO_TO_PATH',
  SET_RAW_TEXT: 'SET_RAW_TEXT',
  SET_PREVIEW_RUN: 'SET_PREVIEW_RUN',
};

const initialUiState = {
  // Collapsed by default: the dominant task is duplicate-and-tweak, and four one-line summaries
  // read faster than four open forms. The panel a [Go →] targets opens itself.
  expandedPanels: {},
  panelModes: {},
  activePath: null,
  // The raw panes' text buffers live HERE rather than in the pane, because the pane unmounts on a
  // mode toggle, a header collapse, or a [Go] from the Problems strip — and unparsed text that
  // disappears on collapse is work the operator loses with no warning.
  rawText: {},
  // The last preview run lives HERE, not in the dock, for the same reason the raw panes' text does:
  // the dock unmounts on a panel collapse or a `{ } JSON` toggle (QuestionPanel.js:179, :199) and
  // panels start collapsed. That was a shrug when a run was two documents; since fapi #492 a run is
  // up to 25 model calls of real spend, and losing it to a stray click is losing money.
  previewRun: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.TOGGLE_PANEL:
      return {
        ...state,
        expandedPanels: { ...state.expandedPanels, [action.panel]: !state.expandedPanels[action.panel] },
      };
    case ACTIONS.EXPAND_PANEL:
      return { ...state, expandedPanels: { ...state.expandedPanels, [action.panel]: true } };
    case ACTIONS.SET_PANEL_MODE:
      return { ...state, panelModes: { ...state.panelModes, [action.panel]: action.mode } };
    case ACTIONS.GO_TO_PATH:
      return {
        ...state,
        activePath: action.path,
        expandedPanels: action.panel ? { ...state.expandedPanels, [action.panel]: true } : state.expandedPanels,
        // A [Go →] always lands the operator on a control, so the target panel leaves `{ }` mode.
        panelModes: action.panel ? { ...state.panelModes, [action.panel]: 'form' } : state.panelModes,
      };
    case ACTIONS.SET_RAW_TEXT:
      return { ...state, rawText: { ...state.rawText, [action.panel]: action.text } };
    case ACTIONS.SET_PREVIEW_RUN:
      return { ...state, previewRun: action.run };
    default:
      return state;
  }
};

export const SpecEditorProvider = ({
  definition,
  setValue,
  removeValue,
  replaceSection,
  seedOrigin,
  bufferEpoch = 0,
  savedEpoch = 0,
  children,
}) => {
  const [ui, dispatch] = React.useReducer(reducer, initialUiState);

  const problems = React.useMemo(() => validateDefinition(definition), [definition]);
  const provides = React.useMemo(
    () => providesFor(definition && definition.trigger && definition.trigger.kind),
    [definition]
  );
  const changes = React.useMemo(() => changesBySection(definition, seedOrigin), [definition, seedOrigin]);

  const togglePanel = React.useCallback((panel) => dispatch({ type: ACTIONS.TOGGLE_PANEL, panel }), []);
  const expandPanel = React.useCallback((panel) => dispatch({ type: ACTIONS.EXPAND_PANEL, panel }), []);
  const setPanelMode = React.useCallback((panel, mode) => dispatch({ type: ACTIONS.SET_PANEL_MODE, panel, mode }), []);
  const goToPath = React.useCallback((path, panel) => dispatch({ type: ACTIONS.GO_TO_PATH, path, panel }), []);
  const setRawText = React.useCallback(
    (panel, entry) => dispatch({ type: ACTIONS.SET_RAW_TEXT, panel, text: entry }),
    []
  );
  const setPreviewRun = React.useCallback((run) => dispatch({ type: ACTIONS.SET_PREVIEW_RUN, run }), []);

  // A preview run describes the SAVED row it compiled, so it survives exactly as long as that row
  // does. savedEpoch — not bufferEpoch — is the signal: a save or a reload replaces the row and
  // makes the run a description of a spec that no longer exists, while a revert replaces only the
  // buffer and leaves the run perfectly valid. Clearing on bufferEpoch would have thrown away up to
  // 25 model calls of real spend every time someone pressed 'Revert this section'.
  const lastSavedEpoch = React.useRef(savedEpoch);
  React.useEffect(() => {
    if (savedEpoch === lastSavedEpoch.current) return;
    lastSavedEpoch.current = savedEpoch;
    dispatch({ type: ACTIONS.SET_PREVIEW_RUN, run: null });
  }, [savedEpoch]);

  const value = React.useMemo(
    () => ({
      definition,
      setValue,
      removeValue,
      replaceSection,
      problems,
      provides,
      changesBySection: changes,
      seedOrigin,
      activePath: ui.activePath,
      goToPath,
      expandedPanels: ui.expandedPanels,
      togglePanel,
      expandPanel,
      panelModes: ui.panelModes,
      setPanelMode,
      rawText: ui.rawText,
      setRawText,
      bufferEpoch,
      savedEpoch,
      previewRun: ui.previewRun,
      setPreviewRun,
      unparsedPanels: Object.keys(ui.rawText).filter(
        (panel) => ui.rawText[panel] && ui.rawText[panel].epoch === bufferEpoch && ui.rawText[panel].error
      ),
    }),
    [
      definition,
      setValue,
      removeValue,
      replaceSection,
      problems,
      provides,
      changes,
      seedOrigin,
      ui.activePath,
      ui.expandedPanels,
      ui.panelModes,
      ui.rawText,
      ui.previewRun,
      setPreviewRun,
      bufferEpoch,
      savedEpoch,
      goToPath,
      togglePanel,
      expandPanel,
      setPanelMode,
      setRawText,
    ]
  );

  return <SpecEditorContext.Provider value={value}>{children}</SpecEditorContext.Provider>;
};
